// --- Core
import { getNestedProperty, HIGH_BG_CLASS, LOW_BG_CLASS, MED_BG_CLASS } from './DsHelper.js';
import AdpHelper from '../helpers/AdpHelper.js';
import DefaultScoringHelper from './../helpers/DefaultScoringHelper.js';
import { getUrl } from './PlayerHelper.js';
import { getPercentHighLevel, getPercentLowLevel } from './ProjectionGenericHelper.js';
import { getPickDisplay } from './DraftHelper.js';
import { getAvgRemainingSosBgClass } from './StrengthOfScheduleHelper.js';

const posRanksMemo = new Map();
export const getPositionalRanks = (projections, sortKey) => {
    if (posRanksMemo.has(sortKey)) {
        return posRanksMemo.get(sortKey);
    }
    // Keyed by sortKey (dsValue, weekly3dPts, etc), subkeyed by player fantasy_position
    const positionalRanks = {};

    // Sort the projections by the sortKey
    const sortedProjections = projections.slice().sort((a, b) => {
        const aVal = getNestedProperty(a, sortKey);
        const bVal = getNestedProperty(b, sortKey);
        return bVal - aVal;
    });

    // Build the positional ranks
    for (let i = 0; i < sortedProjections.length; i++) {
        const player = sortedProjections[i]['player'];
        if (!positionalRanks[player.fantasy_position]) {
            positionalRanks[player.fantasy_position] = [];
        }
        positionalRanks[player.fantasy_position].push(player.id);
    }

    posRanksMemo.set(sortKey, positionalRanks);

    return positionalRanks;
};
export const teamColors = {
    // -- Team Colors - https://teamcolors.jim-nielsen.com/
    ari: '#97233F',
    atl: '#A71930',
    bal: '#241773',
    buf: '#00338D',
    car: '#0085CA',
    chi: '#0B162A',
    cin: '#FB4F14',
    cle: '#FB4F14',
    dal: '#002244',
    den: '#FB4F14',
    det: '#005A8B',
    gb: '#203731',
    hou: '#A71930',
    ind: '#002C5F',
    jax: '#006778',
    kc: '#E31837',
    lar: '#002244',
    mia: '#008E97',
    min: '#4F2683',
    ne: '#002244',
    no: '#000',
    nyg: '#0B2265',
    nyj: '#203731',
    lvr: '#A5ACAF',
    phi: '#004953',
    pit: '#000000',
    lac: '#002244',
    sea: '#002244',
    sf: '#AA0000',
    tb: '#D50A0A',
    ten: '#002244',
    wsh: '#773141',
};

export const getFloorClassProjectionHelper = function (projection, lowRangePoints) {
    const percent_low = projection.percent_low;
    const fantasyPosition = Object.hasOwnProperty(projection, 'player') ? projection.player.fantasy_position : null;
    return fantasyPosition ? getFloorClass(percent_low, fantasyPosition, lowRangePoints) : '';
};
/**
 * Determines the CSS class for the floor based on the player's weekly prediction
 * If player has a current weekly prediction, returns a class based on the percent low level
 * If no prediction exists, returns 'floor-mid'
 * @returns {string} CSS class name for floor styling
 */
export const getFloorClass = function (percent_low, fantasy_position, lowRangePoints) {
    return 'floor-' + getPercentLowLevel(percent_low, fantasy_position, lowRangePoints);
};

export const getCeilingClassProjectionHelper = function (projection, highRangePoints) {
    const percent_high = projection.percent_high;
    const fantasyPosition = Object.hasOwnProperty(projection, 'player') ? projection.player.fantasy_position : null;
    return fantasyPosition ? getCeilingClass(percent_high, fantasyPosition, highRangePoints) : '';
};
/**
 * Determines the CSS class for the ceiling level based on player's weekly prediction
 * @returns {string} CSS class name representing the ceiling level (e.g., 'ceiling-high', 'ceiling-mid', etc.)
 * @depends getPercentHighLevel
 * @depends props.player.currentWeeklyPrediction
 * @depends props.player.fantasy_position
 * @depends siteOptionCache.percentHighLowRanges.highRangePoints
 */
export const getCeilingClass = function (percent_high, fantasy_position, highRangePoints) {
    return 'ceiling-' + getPercentHighLevel(percent_high, fantasy_position, highRangePoints);
};

export const positionalRiskGroupOptions = ['Very Low Risk', 'Low Risk', 'Medium Risk', 'High Risk', 'Very High Risk'];

/**
 * @var array Values for risk group as keys and css classes as values
 */
export const positionalRiskGroupBgClasses = ['green-bg', 'green-bg', 'yellow-bg', 'red-bg', 'red-bg'];

/**
 * @var array Rank-band pill classes for injury risk groups 1–5 (very low → very high).
 */
const positionalRiskGroupPillClasses = [
    HIGH_BG_CLASS,
    HIGH_BG_CLASS,
    MED_BG_CLASS,
    LOW_BG_CLASS,
    LOW_BG_CLASS,
];

/**
 * @var array Values for risk group as keys and css classes as values
 */
export const positionalRiskGroupTextClasses = ['green-text high-green-text', 'green-text low-green-text', 'yellow-text', 'red-text low-red-text', 'red-text high-red-text'];

/**
 * Returns the field name for percentage difference statistics based on fantasy position
 * @param {string} fantasyPosition - The fantasy position (QB, RB, WR, TE, K, DEF, DB, LB, DL, IDP FLEX, TQB, TK)
 * @returns {(string|boolean)} Field name for the position's percentage difference stat, or false for head coaches
 */
export const getAgainstPercentageField = function (fantasyPosition) {
    switch (fantasyPosition.toUpperCase()) {
        case 'QB':
        case 'RB':
        case 'WR':
        case 'TE':
        case 'K':
        case 'DEF':
            return 'against_' + fantasyPosition.toLowerCase() + '_percent_diff';
        case 'DB':
        case 'LB':
        case 'DL':
        case 'IDP FLEX':
            return 'against_def_percent_diff';
        case 'TQB':
            return 'against_qb_percent_diff';
        case 'TK':
            return 'against_k_percent_diff';
    }
    // --- Head coaches will not have any value
    return false;
};
/**
 * Determines CSS class based on Strength of Schedule/Fantasy Points Against value
 * @param {number} value - The SoS/FPA value to evaluate
 * @returns {string} CSS class name ('red-text' for values < -0.1, 'green-text' for values > 0.1, '' for values in between)
 */
export const getSosFpaClass = function (value) {
    let level = -1;
    if (value < -0.1) {
        level = 0;
    } else if (value > 0.1) {
        level = 1;
    }
    if (level === 0) {
        return 'red-text';
    } else if (level === 1) {
        return 'green-text';
    } else {
        return '';
    }
};

export const applyHeaderClass = function (col) {
    if (col.headerClass) {
        return applyCssClass({}, col, 'headerClass');
    }
    // If `class` is a function we cannot use it for header because we don't have the projection.
    if (typeof col.class === 'function') {
        return '';
    }
    return col.class;
};

export const applyCssClass = (row, col, classAttr) => {
    if ((col[classAttr] && typeof col[classAttr] === 'object') || typeof col.class === 'string') {
        return col[classAttr];
    }
    if (typeof col[classAttr] === 'function') {
        return col[classAttr](row);
    }
    return '';
};

/**
 * @function sortDsValue
 * @param {Object} projection - The projection object to sort
 * @return {number} - The adjusted value for sorting
 * @description This function sorts the projection based on the selected scoring configuration and the tier key.
 * It calculates the adjusted value by checking if the dsValue is negative and adjusting it accordingly.
 * The function returns a number that is used for sorting the projections.
 * If the dsValue is not found, it returns 0.
 */
export const sortDsValueWithTiers = (projection, dsValueKey, tierKey) => {
    try {
        const dsValue = projection[dsValueKey];
        let tierValue = projection[tierKey] ?? null;
        if (tierValue === null) {
            return 1000000 - dsValue;
        }
        if (tierValue <= 0) {
            tierValue = 15;
        }
        let sortValue = 0;
        if (dsValue <= 0) {
            // For zero or negative values, add instead of subtract
            sortValue = tierValue * 1000000 + Math.abs(dsValue);
        } else {
            // For positive values, subtract as normal
            sortValue = tierValue * 1000000 - dsValue;
        }
        return sortValue;
    } catch {
        return 0;
    }
};

/**
 * @param {object|number|null|undefined} sipOrGroup SIP profile or positional_risk_group (1–5)
 * @returns {number|null}
 */
export const getValidPositionalRiskGroup = function (sipOrGroup) {
    if (sipOrGroup == null) {
        return null;
    }
    const group =
        typeof sipOrGroup === 'number' ? sipOrGroup : Number(sipOrGroup?.positional_risk_group);
    if (!Number.isFinite(group) || group < 1 || group > positionalRiskGroupOptions.length) {
        return null;
    }
    return group;
};

/** RankingsTable body cell presentation (declare on column defs in ColumnDefinitionHelper). */
export const RANKINGS_CELL_TYPE_PILL_STAT = 'pill-stat';
export const RANKINGS_CELL_TYPE_INJURY_RISK_BAND = 'injury-risk-band';

/** @param {object|null|undefined} projection */
export const resolveSipPlayerProfile = projection =>
    projection?.sipPlayerProfile ?? projection?.player?.sipPlayerProfile ?? null;

export const getInjuryRiskClass = function (sipOrGroup) {
    const group = getValidPositionalRiskGroup(sipOrGroup);
    if (group === null) {
        return '';
    }
    return positionalRiskGroupPillClasses[group - 1] ?? MED_BG_CLASS;
};

/**
 * Injury Risk band cell (injury-predictor): label + legacy text class + rank background class.
 *
 * @param {object|null|undefined} projection Rankings row / player projection
 * @returns {{ text: string, spanClass: string, bgClass: string }}
 */
export const getInjuryRiskCell = function (projection) {
    const group = getValidPositionalRiskGroup(resolveSipPlayerProfile(projection));
    if (group === null) {
        return { text: '', spanClass: '', bgClass: '' };
    }
    return {
        text: positionalRiskGroupOptions[group - 1] ?? '',
        spanClass: positionalRiskGroupTextClasses[group - 1] ?? '',
        bgClass: positionalRiskGroupPillClasses[group - 1] ?? '',
    };
};

/** @param {{ cellType?: string }} col */
export const isRankingsPillStatColumn = col => col?.cellType === RANKINGS_CELL_TYPE_PILL_STAT;

/** @param {{ cellType?: string }} col */
export const isRankingsInjuryRiskBandColumn = col => col?.cellType === RANKINGS_CELL_TYPE_INJURY_RISK_BAND;

/** @param {object} projection @param {{ cellType?: string }} col */
export const getInjuryRiskPillSpanClass = (projection, col) =>
    isRankingsPillStatColumn(col) ? getInjuryRiskClass(resolveSipPlayerProfile(projection)) : '';

/** @param {object} projection @param {{ cellType?: string }} col */
export const getInjuryRiskBandSpanClass = (projection, col) =>
    isRankingsInjuryRiskBandColumn(col) ? getInjuryRiskCell(projection).spanClass : '';

export const getCellValue = function (projection, col) {
    if (col.fn && typeof col.fn === 'function') {
        return col.fn(projection);
    }
    return getNestedProperty(projection, col.key || '');
};

export const getAdpValue = function (selectedFantasyPosition, selectedScoringConfig, adps, returnOverallPickNumber = false) {
    let adp;
    if (selectedFantasyPosition.toLowerCase() == 'idp') {
        adp = AdpHelper.findMatchingRecord({ source_id: AdpHelper.SOURCE_ID_SLEEPER, format_id: AdpHelper.FORMAT_ID_IDP }, adps);
    } else if (selectedScoringConfig.leagueType === 'bestball') {
        adp = AdpHelper.findMatchingRecord({ source_id: AdpHelper.SOURCE_ID_UNDERDOG, format_id: AdpHelper.FORMAT_ID_BEST_BALL }, adps);
    } else {
        switch (selectedScoringConfig.pprSuperflexSlug) {
            case DefaultScoringHelper.SCORING_FORMAT_SLUG_NO_PPR_NO_SUPERFLEX:
                adp = AdpHelper.findMatchingRecord(
                    { source_id: AdpHelper.SOURCE_ID_FF_NERDS, format_id: AdpHelper.FORMAT_ID_STANDARD },
                    adps
                );
                break;
            case DefaultScoringHelper.SCORING_FORMAT_SLUG_HALF_PPR_NO_SUPERFLEX:
                adp = AdpHelper.findMatchingRecord(
                    { source_id: AdpHelper.SOURCE_ID_FF_NERDS, format_id: AdpHelper.FORMAT_ID_HALF_PPR },
                    adps
                );
                break;
            case DefaultScoringHelper.SCORING_FORMAT_SLUG_ONE_PPR_NO_SUPERFLEX:
                adp = AdpHelper.findMatchingRecord({ source_id: AdpHelper.SOURCE_ID_FF_NERDS, format_id: AdpHelper.FORMAT_ID_PPR }, adps);
                break;
            case DefaultScoringHelper.SCORING_FORMAT_SLUG_TE_PREMIUM_NO_SUPERFLEX:
                adp = AdpHelper.findMatchingRecord(
                    { source_id: AdpHelper.SOURCE_ID_FFPC, format_id: AdpHelper.FORMAT_ID_FFPC_MAIN_EVENT },
                    adps
                );
                break;
            case DefaultScoringHelper.SCORING_FORMAT_SLUG_NO_PPR_SUPERFLEX:
            case DefaultScoringHelper.SCORING_FORMAT_SLUG_HALF_PPR_SUPERFLEX:
            case DefaultScoringHelper.SCORING_FORMAT_SLUG_TE_PREMIUM_SUPERFLEX:
                adp = AdpHelper.findMatchingRecord(
                    { source_id: AdpHelper.SOURCE_ID_SLEEPER, format_id: AdpHelper.FORMAT_ID_SUPERFLEX },
                    adps
                );
                break;
        }
    }
    if (!adp && !returnOverallPickNumber) {
        return "N/A";
    }
    if (!adp && returnOverallPickNumber) {
        return null;
    }
    if (returnOverallPickNumber) {
        return adp.overall_pick_number
    }
    return getPickDisplay(adp.overall_pick_number, adp.league_size);
};

export const getBottomLine = function (projection) {
    const bottomLineRaw = projection.player.bottom_line;
    const url = getUrl(projection.player);
    if (bottomLineRaw) {
        const bottomLineWithLink = `${bottomLineRaw.slice(
            0,
            -4
        )} <a href="${url}" class="bottom-line-profile-link">&nbsp;Read Full Profile ></a></p>`;
        return bottomLineWithLink;
    } else {
        return `<a href="${url}" class="bottom-line-profile-link">&nbsp;Read Full Profile ></a>`;
    }
};
export const getSosClass = function (sosDataArr, projection, position, teamId) {
    if (!sosDataArr) return '';
    const teamSosData = sosDataArr.filter(sosData => sosData.team_id === teamId)[0];
    if (!teamSosData) {
        return '';
    }
    const againstPctField = getAgainstPercentageField(position);
    return getSosFpaClass(teamSosData[againstPctField]);
};

/**
 * Returns a rank-band pill class for average remaining SOS (rankings table cells).
 */
export const getAvgRemainingSosPillClass = function (sosDataArr, position, teamId) {
    if (!sosDataArr?.length) {
        return '';
    }
    const teamSosData = sosDataArr.find(sosData => sosData.team_id === teamId);
    if (!teamSosData) {
        return '';
    }
    const againstPctField = getAgainstPercentageField(position);
    return getAvgRemainingSosBgClass(teamSosData[againstPctField]);
};

export const getTeamAbbreviation = function (obj) {
    if (obj.hasOwnProperty('team') && obj.team.hasOwnProperty('abbr')) {
        return obj.team.abbr;
    } else if (obj.hasOwnProperty('player') && obj.player.hasOwnProperty('team')) {
        return obj.player.team.abbr;
    } else if (obj.hasOwnProperty('team_abbr')) {
        return obj.team_abbr;
    } else if (obj.hasOwnProperty('abbr')) {
        return obj.abbr;
    } else {
        return '';
    }
}
export const getPlayerId = function (obj) {
    if (obj.hasOwnProperty('player_id')) {
        return obj.player_id;
    } else if (obj.hasOwnProperty('player') && obj.player.hasOwnProperty('id')) {
        return obj.player.id;
    } else if (obj.hasOwnProperty('id')) {
        return obj.id;
    } else {
        return null;
    }
}
